import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyProfile = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    cpf: '',
    phone: '',
    email: '',
    address: {
      cep: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: ''
    }
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customer/profile`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setProfile(response.data);
    } catch (error) {
      console.error('Erro ao buscar perfil:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddress = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    
    if (cleanCep.length !== 8) {
      return;
    }

    setCepLoading(true);
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cleanCep}/json/`);
      
      if (response.data.erro) {
        alert('CEP não encontrado');
        return;
      }

      setProfile(prev => ({
        ...prev,
        address: {
          ...prev.address,
          cep: cleanCep,
          street: response.data.logradouro,
          neighborhood: response.data.bairro,
          city: response.data.localidade,
          state: response.data.uf
        }
      }));
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      alert('Erro ao buscar CEP');
    } finally {
      setCepLoading(false);
    }
  };

  const handleCepChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length <= 8) {
      if (value.length > 5) {
        value = value.replace(/(\d{5})(\d)/, '$1-$2');
      }
      setProfile(prev => ({
        ...prev,
        address: { ...prev.address, cep: value }
      }));

      if (value.replace(/\D/g, '').length === 8) {
        fetchAddress(value);
      }
    }
  };

  const formatCPF = (value) => {
    value = value.replace(/\D/g, '');
    if (value.length <= 11) {
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return value;
  };

  const formatPhone = (value) => {
    value = value.replace(/\D/g, '');
    if (value.length <= 11) {
      value = value.replace(/(\d{2})(\d)/, '($1) $2');
      value = value.replace(/(\d{5})(\d)/, '$1-$2');
    }
    return value;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(
        `${BACKEND_URL}/api/customer/profile`,
        {
          name: profile.name,
          cpf: profile.cpf.replace(/\D/g, ''),
          phone: profile.phone.replace(/\D/g, ''),
          address: {
            ...profile.address,
            cep: profile.address.cep.replace(/\D/g, '')
          }
        },
        {
          headers: { 'Authorization': `Bearer ${user.token}` }
        }
      );

      alert('Dados atualizados com sucesso!');
      
      // Atualizar nome no localStorage se mudou
      if (profile.name !== user.name) {
        localStorage.setItem('customer_name', profile.name);
        window.location.reload();
      }
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      alert(error.response?.data?.detail || 'Erro ao atualizar dados');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-8">
        <p className="text-center text-gray-500">Carregando dados...</p>
      </Card>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Meus Dados</h2>
      
      <Card className="p-6">
        <form onSubmit={handleSave} className="space-y-6">
          {/* Personal Info */}
          <div>
            <h3 className="font-semibold text-lg text-gray-800 mb-4">Informações Pessoais</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Completo *
                </label>
                <Input
                  required
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF *
                </label>
                <Input
                  required
                  value={profile.cpf}
                  onChange={(e) => setProfile({ ...profile, cpf: formatCPF(e.target.value) })}
                  maxLength={14}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone *
                </label>
                <Input
                  required
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: formatPhone(e.target.value) })}
                  maxLength={15}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <Input
                  value={profile.email}
                  disabled
                  className="bg-gray-100 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">Email não pode ser alterado</p>
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="pt-6 border-t">
            <h3 className="font-semibold text-lg text-gray-800 mb-4">Endereço</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CEP *
                </label>
                <Input
                  required
                  value={profile.address.cep}
                  onChange={handleCepChange}
                  placeholder="00000-000"
                  maxLength={9}
                  disabled={cepLoading}
                />
                {cepLoading && <p className="text-xs text-gray-500 mt-1">Buscando...</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número *
                </label>
                <Input
                  required
                  value={profile.address.number}
                  onChange={(e) => setProfile({
                    ...profile,
                    address: { ...profile.address, number: e.target.value }
                  })}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rua *
                </label>
                <Input
                  required
                  value={profile.address.street}
                  onChange={(e) => setProfile({
                    ...profile,
                    address: { ...profile.address, street: e.target.value }
                  })}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Complemento
                </label>
                <Input
                  value={profile.address.complement}
                  onChange={(e) => setProfile({
                    ...profile,
                    address: { ...profile.address, complement: e.target.value }
                  })}
                  placeholder="Apto, bloco, etc (opcional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bairro *
                </label>
                <Input
                  required
                  value={profile.address.neighborhood}
                  onChange={(e) => setProfile({
                    ...profile,
                    address: { ...profile.address, neighborhood: e.target.value }
                  })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cidade *
                </label>
                <Input
                  required
                  value={profile.address.city}
                  onChange={(e) => setProfile({
                    ...profile,
                    address: { ...profile.address, city: e.target.value }
                  })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado *
                </label>
                <Input
                  required
                  value={profile.address.state}
                  onChange={(e) => setProfile({
                    ...profile,
                    address: { ...profile.address, state: e.target.value }
                  })}
                  maxLength={2}
                  placeholder="SP"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-6 border-t">
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#00A86B] hover:bg-[#008c5a] text-white px-8"
            >
              <Save size={20} className="mr-2" />
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default MyProfile;